import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Paperclip, Download, X, Eye } from 'lucide-react'
import { useState } from 'react'
import { FilePreviewDialog } from './file-preview-dialog'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { getMaturityColorForLevel, getMaturityNameForLevel } from '@/features/maturity-levels/maturity-color-utils'
import type { MaturityLevel } from '@/features/maturity-levels/types'



interface AnswerOption {
    value: number
    label: string
    description?: string
}

interface Question {
    id: string
    domain_id: string
    domain_name?: string
    category: string
    text: string
    description?: string
    maturity_level?: number
    answer_options?: AnswerOption[]
}

interface QuestionCardProps {
    question: Question
    questionNumber: number
    totalQuestions: number
    answer?: number
    comment?: string
    existingAttachment?: string
    attachment?: File | null
    onAnswerChange: (value: number) => void
    onCommentChange: (comment: string) => void
    onFileChange: (file: File | null) => void
    onDownloadFile: (questionId: string) => void
    readOnly: boolean
    assessmentId: string
}

export function QuestionCard({
    question,
    questionNumber,
    totalQuestions,
    answer,
    comment,
    existingAttachment,
    attachment,
    onAnswerChange,
    onCommentChange,
    onFileChange,
    onDownloadFile,
    readOnly,
    assessmentId,
}: QuestionCardProps) {
    const { data: maturityLevelsData } = useEngineMaturityLevels()
    const levels: MaturityLevel[] = (maturityLevelsData as any)?.data ?? maturityLevelsData ?? []
    const levelColor = getMaturityColorForLevel(levels, question.maturity_level)
    const levelName = getMaturityNameForLevel(levels, question.maturity_level)

    const [previewOpen, setPreviewOpen] = useState(false)

    return (
        <Card className="border-l-4 shadow-md" style={{ borderLeftColor: question.maturity_level ? levelColor : undefined }}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                        Question {questionNumber} of {totalQuestions}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {question.maturity_level && (
                            <span
                                className="text-xs font-medium px-2 py-0.5 rounded border"
                                style={{
                                    color: levelColor,
                                    borderColor: levelColor,
                                    backgroundColor: `${levelColor}10`,
                                }}
                            >
                                Level {question.maturity_level} — {levelName}
                            </span>
                        )}
                        {question.category && (
                            <span className="text-xs font-medium text-muted-foreground bg-gray-100 px-2 py-1 rounded">
                                {question.category}
                            </span>
                        )}
                    </div>
                </div>
                <CardDescription className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {question.text}
                </CardDescription>
                {question.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                        {question.description}
                    </p>
                )}
            </CardHeader>

            <CardContent className="space-y-4 pt-0">
                {/* Answer Options */}
                <RadioGroup
                    value={answer?.toString() || ''}
                    onValueChange={readOnly ? undefined : (value) => onAnswerChange(parseInt(value))}
                    className="space-y-2"
                    disabled={readOnly}
                >
                    {(question.answer_options || []).map((option) => (
                        <div
                            key={option.value}
                            className={`flex items-start space-x-3 rounded-lg border p-3 transition-colors ${answer === option.value
                                ? 'border-blue-300 bg-blue-50/50'
                                : 'border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            <RadioGroupItem
                                value={option.value.toString()}
                                id={`${question.id}-${option.value}`}
                                className="mt-0.5"
                            />
                            <Label htmlFor={`${question.id}-${option.value}`} className="flex-1 cursor-pointer">
                                <div className="font-medium text-sm">{option.label}</div>
                                {option.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>
                                )}
                            </Label>
                        </div>
                    ))}
                </RadioGroup>

                {/* Comment Section */}
                <div className="space-y-1.5">
                    <Label
                        htmlFor={`comment-${question.id}`}
                        className="text-xs font-medium text-muted-foreground"
                    >
                        Additional Comments {readOnly ? '' : '(Optional)'}
                    </Label>
                    <Textarea
                        id={`comment-${question.id}`}
                        placeholder={readOnly ? 'No comments' : 'Add any additional comments or context...'}
                        value={comment || ''}
                        onChange={readOnly ? undefined : (e) => onCommentChange(e.target.value)}
                        readOnly={readOnly}
                        className={`min-h-[60px] text-sm ${readOnly ? 'bg-gray-50' : ''}`}
                    />
                </div>

                {/* File Attachment Section */}
                {(!readOnly || existingAttachment) && (
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">
                            File Attachment {readOnly ? '' : '(Optional)'}
                        </Label>

                        {existingAttachment && (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-sm text-gray-700 flex-1">{existingAttachment}</span>
                                <div className="flex items-center gap-1">
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => setPreviewOpen(true)}
                                        className="h-8 w-8 p-0"
                                        title="Preview attachment"
                                    >
                                        <Eye className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => onDownloadFile(question.id)}
                                        className="h-8 w-8 p-0"
                                        title="Download attachment"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                                <FilePreviewDialog 
                                    open={previewOpen}
                                    onOpenChange={setPreviewOpen}
                                    assessmentId={assessmentId}
                                    questionId={question.id}
                                    filename={existingAttachment}
                                />
                            </div>
                        )}

                        {!readOnly && (
                            <div className="flex items-center gap-2">
                                <Input
                                    type="file"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] || null
                                        onFileChange(file)
                                    }}
                                    className="hidden"
                                    id={`file-${question.id}`}
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const fileInput = document.getElementById(`file-${question.id}`) as HTMLInputElement
                                        fileInput?.click()
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <Paperclip className="h-3.5 w-3.5" />
                                    {attachment ? 'Change File' : 'Attach File'}
                                </Button>
                                {attachment && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <span>{attachment.name}</span>
                                        <Button variant="ghost" size="sm" onClick={() => onFileChange(null)}>
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
